import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Report = {
  ok: boolean;
  mode: string;
  account?: { id: string; business_name?: string } | null;
  webhook_secret_present: boolean;
  last_processed?: { type: string; processed_at: string; stripe_event_id: string; livemode: boolean } | null;
  totals: { total_24h: number; processed_24h: number; failed_24h: number; received_24h: number; live_24h: number };
  recent_failures: { id: string; stripe_event_id: string; type: string; livemode: boolean; error: string; received_at: string }[];
  checks: { name: string; ok: boolean; detail?: string }[];
  webhook_url: string;
};

export default function AdminStripeWebhookStatus() {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const filteredFailures = useMemo(() => {
    const list = report?.recent_failures ?? [];
    if (!q.trim()) return list;
    const needle = q.toLowerCase();
    return list.filter(f =>
      [f.type, f.error, f.stripe_event_id].some(v => (v ?? "").toLowerCase().includes(needle))
    );
  }, [report, q]);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-readiness");
      if (error) throw error;
      setReport(data as Report);
    } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  return (
    <AdminShell title="STRIPE WEBHOOK STATUS" actions={
      <Button variant="outline" onClick={load} disabled={loading}>
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Refresh
      </Button>
    }>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Mode" value={(report?.mode ?? "—").toUpperCase()} tone={report?.mode === "live" ? "good" : "muted"} />
        <Stat label="Signature secret" value={report?.webhook_secret_present ? "OK" : "Missing"} tone={report?.webhook_secret_present ? "good" : "bad"} />
        <Stat label="Processed (24h)" value={String(report?.totals?.processed_24h ?? 0)} tone="good" />
        <Stat label="Failed (24h)" value={String(report?.totals?.failed_24h ?? 0)} tone={(report?.totals?.failed_24h ?? 0) === 0 ? "good" : "bad"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-panel border border-border p-5">
          <h3 className="font-display tracking-widest text-xs mb-4">VERIFICATION CHECKS</h3>
          {!report ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            <ul className="space-y-2 text-sm">
              {report.checks.map((c, i) => (
                <li key={i} className="flex items-start gap-2">
                  {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> : <XCircle className="w-4 h-4 text-destructive mt-0.5" />}
                  <div>
                    <p className="font-body">{c.name}</p>
                    {c.detail && <p className="text-xs text-muted-foreground font-mono">{c.detail}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 pt-4 border-t border-border text-xs font-mono break-all text-muted-foreground">
            Endpoint: {report?.webhook_url ?? "—"}
          </div>
        </div>

        <div className="bg-panel border border-border p-5">
          <h3 className="font-display tracking-widest text-xs mb-4">LAST PROCESSED EVENT</h3>
          {report?.last_processed ? (
            <div className="text-sm space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant={report.last_processed.livemode ? "default" : "secondary"}>
                  {report.last_processed.livemode ? "LIVE" : "TEST"}
                </Badge>
                <span className="font-mono text-xs">{report.last_processed.type}</span>
              </div>
              <p className="font-mono text-xs text-muted-foreground break-all">{report.last_processed.stripe_event_id}</p>
              <p className="font-mono text-xs">{new Date(report.last_processed.processed_at).toLocaleString()}</p>
            </div>
          ) : <p className="text-muted-foreground text-sm">No events processed yet.</p>}

          <div className="mt-6 mb-3 flex items-center justify-between gap-3">
            <h3 className="font-display tracking-widest text-xs">RECENT FAILURES</h3>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search type, event id, error…"
              className="h-8 text-xs max-w-[240px]"
            />
          </div>
          {(report?.recent_failures?.length ?? 0) === 0 ? (
            <p className="text-sm text-emerald-500/80 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> None</p>
          ) : filteredFailures.length === 0 ? (
            <p className="text-sm text-muted-foreground">No matches for "{q}".</p>
          ) : (
            <ul className="space-y-3 text-xs">
              {filteredFailures.map((f) => (
                <li key={f.id} className="border-l-2 border-destructive pl-3">
                  <div className="flex justify-between gap-2">
                    <span className="font-mono">{f.type}</span>
                    <span className="text-muted-foreground">{new Date(f.received_at).toLocaleString()}</span>
                  </div>
                  <p className="font-mono text-[10px] text-muted-foreground break-all mt-0.5">{f.stripe_event_id}</p>
                  <p className="text-destructive font-mono break-words mt-1">{f.error}</p>
                  <a href={`/admin/stripe/events`} className="text-primary inline-flex items-center gap-1 mt-1">
                    View event <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: "good" | "bad" | "muted" }) {
  const c = tone === "good" ? "text-emerald-400" : tone === "bad" ? "text-destructive" : "text-foreground";
  return (
    <div className="bg-panel border border-border p-4">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`font-display text-2xl mt-1 ${c}`}>{value}</p>
    </div>
  );
}